<template>
  <GroundStationStatusIcon
    :icon="buttonIcon"
    :tone="buttonTone"
    :title="$t(buttonTitle)"
    :loading="voiceListening"
    :active="voiceControlEnabled"
    interactive
    @click="toggleVoiceControl"
  />
</template>

<script>
import { t } from '@/i18n'
import GroundStationStatusIcon from '@/components/ground-station/GroundStationStatusIcon'
import { speakText } from '@/utils/index'
import mqtt from '@/utils/mqtt'
import {
  inferVoiceCommand,
  parseVoiceCommand,
  parseWakePhrase,
  persistLearnedVoiceAlias
} from '@/utils/voiceControl'

export default {
  name: 'TerminalVoiceControl',
  components: { GroundStationStatusIcon },
  props: {
    plane: { type: Object, default: null }
  },
  data () {
    return {
      voiceControlEnabled: false,
      voiceListening: false,
      voiceAwaitingCommand: false,
      voiceError: false,
      voiceErrorMessage: '',
      voiceRestartTimer: null,
      browserVoiceRecognition: null,
      voiceSessionId: 0
    }
  },
  computed: {
    buttonIcon () {
      if (this.voiceListening) return 'el-icon-loading'
      if (this.voiceAwaitingCommand) return 'el-icon-bell'
      return 'iconfont icon-icon-test'
    },
    buttonTone () {
      if (this.voiceError) return 'danger'
      if (!this.voiceControlEnabled) return 'neutral'
      return this.voiceTargetStatus.ready ? 'accent' : 'danger'
    },
    buttonTitle () {
      if (this.voiceError) return this.voiceErrorMessage || '语音控制不可用'
      if (!this.voiceControlEnabled) return '开启飞行魔方语音控制'
      if (!this.voiceTargetStatus.ready) return `语音控制已开启：${this.voiceTargetStatus.reason}`
      if (this.voiceAwaitingCommand) return '请说出控制指令'
      return '语音控制已开启，点击关闭'
    },
    voiceTargetStatus () {
      if (!this.plane?.plane_code) return { ready: false, reason: '请先选择要控制的飞机' }
      if (!this.plane.planeState?.online) return { ready: false, reason: '当前选择的飞机不在线' }
      if (![true, 1, '1'].includes(this.plane.module?.cable_transport)) {
        return { ready: false, reason: '当前飞机没有挂载仓模块' }
      }
      return { ready: true, reason: '' }
    },
    voiceTargetKey () {
      return [
        this.plane?.id || '',
        this.plane?.plane_code || '',
        this.plane?.planeState?.online ? 1 : 0,
        [true, 1, '1'].includes(this.plane?.module?.cable_transport) ? 1 : 0
      ].join(':')
    }
  },
  watch: {
    voiceTargetKey (nextKey, previousKey) {
      if (!this.voiceControlEnabled) return
      if (nextKey === previousKey) return
      this.pauseVoiceSession()
      this.voiceError = false
      this.voiceErrorMessage = ''
      if (!this.voiceTargetStatus.ready) return
      const planeName = this.plane?.name || this.plane?.plane_name || '当前飞机'
      this.$message.success(t(`${planeName}已连接，语音控制开始监听`))
      this.$nextTick(() => this.startVoiceSession('wake'))
    }
  },
  beforeDestroy () {
    this.stopVoiceControl()
  },
  methods: {
    toggleVoiceControl () {
      if (this.voiceControlEnabled) {
        this.stopVoiceControl()
        this.$message.info(t('飞行魔方语音控制已关闭'))
        return
      }
      this.voiceError = false
      this.voiceErrorMessage = ''
      this.voiceControlEnabled = true
      this.voiceAwaitingCommand = false
      if (!this.voiceTargetStatus.ready) {
        this.$message.warning(t(`语音控制已开启，${this.voiceTargetStatus.reason}`))
        return
      }
      this.$message.success(t('语音控制已开启，请说“飞行魔方”唤醒'))
      this.startVoiceSession('wake')
    },
    validateVoiceTarget (notify = true) {
      if (this.voiceTargetStatus.ready) return true
      if (notify) this.$message.warning(t(this.voiceTargetStatus.reason))
      return false
    },
    startVoiceSession (mode) {
      if (!this.voiceControlEnabled || this.voiceListening) return
      if (!this.validateVoiceTarget(false)) {
        this.pauseVoiceSession()
        return
      }
      this.clearVoiceRestartTimer()
      this.voiceAwaitingCommand = mode === 'command'
      this.voiceListening = true
      const sessionId = ++this.voiceSessionId
      const nativePlugin = window.cordova?.plugins?.voiceRecognition
      if (nativePlugin) {
        nativePlugin.startListening(
          results => this.handleVoiceResults(results, mode, sessionId),
          error => this.handleVoiceError(error, mode, sessionId),
          { language: 'zh-CN' }
        )
        return
      }
      const BrowserSpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      if (!BrowserSpeechRecognition) {
        this.handleVoiceError('not_available', mode, sessionId)
        return
      }
      const recognition = new BrowserSpeechRecognition()
      this.browserVoiceRecognition = recognition
      recognition.lang = 'zh-CN'
      recognition.interimResults = false
      recognition.maxAlternatives = 5
      recognition.onresult = event => {
        this.handleVoiceResults(Array.from(event.results[0]).map(item => item.transcript), mode, sessionId)
      }
      recognition.onerror = event => this.handleVoiceError(event.error, mode, sessionId)
      recognition.start()
    },
    handleVoiceResults (results, mode, sessionId) {
      if (sessionId !== this.voiceSessionId || !this.voiceControlEnabled) return
      this.voiceListening = false
      this.browserVoiceRecognition = null
      const candidates = Array.isArray(results) ? results : [results]
      if (mode === 'wake') {
        if (!candidates.some(item => Boolean(parseWakePhrase(item)))) {
          this.scheduleVoiceSession('wake', 250)
          return
        }
        this.voiceAwaitingCommand = true
        this.$message.success(t('飞行魔方已唤醒，请说：放钩、收钩或校准'))
        this.safeSpeakText('在')
        this.scheduleVoiceSession('command', 800)
        return
      }
      let command = null
      let recognizedText = String(candidates[0] || '').trim()
      candidates.some(item => {
        const parsed = parseVoiceCommand(item) || inferVoiceCommand(item)
        if (!parsed) return false
        command = parsed
        recognizedText = String(item || '').trim()
        return true
      })
      if (!command) {
        this.voiceAwaitingCommand = false
        this.$message.warning(t(recognizedText ? `未识别到有效指令：“${recognizedText}”` : '没有听清指令'))
        this.safeSpeakText('没听清')
        this.scheduleVoiceSession('wake', 500)
        return
      }
      if (command.learningCandidate) this.recordVoiceLearning(command)
      this.voiceAwaitingCommand = false
      if (this.validateVoiceTarget()) this.executeVoiceCommand(command)
      this.scheduleVoiceSession('wake', 600)
    },
    recordVoiceLearning (command) {
      if (!command.learnedText) return
      this.$store.commit('recordVoiceLearningCandidate', { text: command.learnedText, key: command.key })
      const candidate = this.$store.state.voiceLearningCandidates[command.learnedText]
      if (!candidate || candidate.key !== command.key || candidate.count < 3) return
      persistLearnedVoiceAlias(command.learnedText, command.key, candidate.count)
      this.$store.commit('removeVoiceLearningCandidate', command.learnedText)
    },
    executeVoiceCommand (command) {
      const payload = {
        hook_down: '{hookConteroller:1}',
        hook_recovery: '{hookConteroller:0}',
        calibrate: '{hookConteroller:4}'
      }[command.key]
      if (!payload || !this.plane) return
      if (command.key === 'calibrate' && this.plane.planeState) this.$set(this.plane.planeState, 'AlignStatus', 0)
      mqtt.publishCommand(this.plane, payload)
      this.$store.dispatch('fetchLog', {
        sourceType: 'terminal',
        planeId: this.plane.id,
        planeName: this.plane.name,
        content: `飞行魔方语音控制：${command.label}`
      })
      this.$message.success(t(`已执行${command.label}`))
      this.safeSpeakText(`已执行${command.label}`)
    },
    handleVoiceError (error, mode, sessionId) {
      if (sessionId !== this.voiceSessionId || !this.voiceControlEnabled) return
      this.voiceListening = false
      this.browserVoiceRecognition = null
      const errorText = String(error || '')
      const fatalMessages = {
        permission_denied: '需要授予麦克风权限才能使用语音控制',
        not_available: '当前设备没有可用的语音识别服务',
        'not-allowed': '需要授予麦克风权限才能使用语音控制'
      }
      if (fatalMessages[errorText]) {
        this.voiceError = true
        this.voiceErrorMessage = fatalMessages[errorText]
        this.stopVoiceControl(true)
        this.$message.error(t(fatalMessages[errorText]))
        return
      }
      if (mode === 'command') {
        this.voiceAwaitingCommand = false
        this.$message.warning(t('没有听清指令，请重新说“飞行魔方”唤醒'))
        this.safeSpeakText('没听清')
      }
      this.scheduleVoiceSession('wake', 400)
    },
    scheduleVoiceSession (mode, delay) {
      if (!this.voiceControlEnabled) return
      this.clearVoiceRestartTimer()
      this.voiceRestartTimer = setTimeout(() => {
        this.voiceRestartTimer = null
        this.startVoiceSession(mode)
      }, delay)
    },
    clearVoiceRestartTimer () {
      if (!this.voiceRestartTimer) return
      clearTimeout(this.voiceRestartTimer)
      this.voiceRestartTimer = null
    },
    stopVoiceControl (keepError = false) {
      this.voiceControlEnabled = false
      if (!keepError) {
        this.voiceError = false
        this.voiceErrorMessage = ''
      }
      this.pauseVoiceSession()
    },
    pauseVoiceSession () {
      this.voiceListening = false
      this.voiceAwaitingCommand = false
      this.voiceSessionId += 1
      this.clearVoiceRestartTimer()
      if (this.browserVoiceRecognition) {
        this.browserVoiceRecognition.abort()
        this.browserVoiceRecognition = null
      }
      const nativePlugin = window.cordova?.plugins?.voiceRecognition
      if (nativePlugin) nativePlugin.stopListening(() => {}, () => {})
    },
    safeSpeakText (text) {
      try {
        speakText(text)
      } catch (error) {
        console.warn('[TerminalVoiceControl] 语音提示播放失败', error)
      }
    }
  }
}
</script>
